import React from 'react';
import { motion } from 'framer-motion';
import '../styles/Pages.css';
import { TermsHero } from '../components/PageHeroes';

const slugify = (title) =>
  title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

const LAST_UPDATED = '19 June 2026';

const Terms = () => {
  const sections = [
    {
      title: 'Acceptance of Terms',
      content: 'By accessing or using Effective Global Staff\'s services, you agree to be bound by these Terms of Service and our Privacy Policy. If you do not agree to these terms, please do not use our services.'
    },
    {
      title: 'Description of Service',
      content: 'Effective Global Staff provides a business management platform covering payroll processing, HR management, invoicing, accounts payable, banking, accounting, VAT filing, appointments, document management, and related services for UK businesses ("the Service").'
    },
    {
      title: 'User Accounts',
      content: 'You must provide accurate and complete information when creating an account. You are responsible for maintaining the confidentiality of your account credentials and for all activities under your account.'
    },
    {
      title: 'Payment Terms',
      content: 'Subscription fees are billed in advance on a monthly or annual basis. All fees are non-refundable except as required by law. We reserve the right to change our pricing with 30 days notice.'
    },
    {
      title: 'Data Accuracy',
      content: 'You are responsible for the accuracy of all data entered into the Service, including payroll, financial, and employee information. We are not liable for errors resulting from inaccurate data provided by you.'
    },
    {
      title: 'Acceptable Use',
      content: 'You agree not to use the Service for any unlawful purpose or in violation of any applicable regulations. You may not attempt to gain unauthorized access to any part of the Service.'
    },
    {
      title: 'Limitation of Liability',
      content: 'To the maximum extent permitted by law, Effective Global Staff shall not be liable for any indirect, incidental, special, or consequential damages arising from your use of the Service.'
    },
    {
      title: 'Termination',
      content: 'We may suspend or terminate your account if you breach these terms. You may cancel your subscription at any time through your account settings.'
    },
    {
      title: 'Governing Law',
      content: 'These terms are governed by the laws of England and Wales. Any disputes shall be subject to the exclusive jurisdiction of the courts of England and Wales.'
    },
    {
      title: 'Contact Us',
      content: 'If you have questions about these Terms of Service, contact us at thomas@effectivestaff.co.uk or write to our UK office.'
    }
  ];

  return (
    <div className="page-container">
      <TermsHero />

      <section className="section-legal">
        <div className="container legal-layout">
          <nav className="legal-toc" aria-label="Terms of service sections">
            <span className="legal-toc-label">On this page</span>
            <ul>
              {sections.map((section, idx) => (
                <li key={idx}>
                  <a href={`#${slugify(section.title)}`}>
                    <span className="legal-toc-number">{String(idx + 1).padStart(2, '0')}</span>
                    {section.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <motion.div
            className="legal-content"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="legal-updated">Last updated: {LAST_UPDATED}</p>

            <p className="intro">
              These Terms of Service ("Terms") govern your access to and use of Effective Global Staff's
              website and application. Please read them carefully before using our services.
            </p>

            {sections.map((section, idx) => (
              <motion.div
                key={idx}
                id={slugify(section.title)}
                className="legal-section"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.05 }}
              >
                <h2>
                  <span className="legal-section-number">{String(idx + 1).padStart(2, '0')}</span>
                  {section.title}
                </h2>
                <p>{section.content}</p>
              </motion.div>
            ))}

            <motion.div
              className="legal-footer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <h3>Changes to These Terms</h3>
              <p>
                We may update these Terms from time to time. We will notify you of any material changes
                by posting the new Terms on this page and updating the "Last updated" date above.
              </p>
              <p>
                <strong>Questions?</strong> Contact us at <a href="mailto:thomas@effectivestaff.co.uk">thomas@effectivestaff.co.uk</a>
              </p>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Terms;